import React, { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Users, Award, Zap, Heart } from 'lucide-react';

export default function About() {
  const { setNeutralTheme } = useTheme();

  useEffect(() => {
    setNeutralTheme();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="about-title">About Zenvy</h1>
          <p className="text-xl text-zinc-400 leading-relaxed">
            We're revolutionizing the way people discover and book salon services. 
            Our platform connects customers with the best salons in their area, 
            making grooming and beauty accessible to everyone.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-zinc-500 uppercase tracking-wider">Our Mission</span>
              <h2 className="text-4xl font-bold text-zinc-900 mt-2 mb-6">
                Making Beauty Accessible
              </h2>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Zenvy was founded with a simple mission: to connect people with quality grooming 
                services in their neighborhood. We believe everyone deserves access to professional 
                beauty and grooming services that make them feel confident and refreshed.
              </p>
              <p className="text-zinc-600 leading-relaxed">
                By partnering with verified salons and providing a seamless booking experience, 
                we're building a community where customers and salon professionals thrive together.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Salon interior"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-zinc-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-zinc-500 uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-2">What We Stand For</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Users, title: 'Community First', desc: 'Building strong relationships between customers and local salons' },
              { icon: Award, title: 'Quality Assured', desc: 'Every salon partner is vetted for excellence in service' },
              { icon: Zap, title: 'Innovation', desc: 'Constantly improving the booking experience with technology' },
              { icon: Heart, title: 'Care', desc: 'Putting the well-being of our users at the heart of everything' }
            ].map((value, i) => (
              <Card key={i} className="border-zinc-200 text-center">
                <CardContent className="p-8">
                  <value.icon className="w-12 h-12 text-zinc-900 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-zinc-900 mb-2">{value.title}</h3>
                  <p className="text-zinc-600 text-sm">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500+', label: 'Partner Salons' },
              { value: '50K+', label: 'Happy Customers' },
              { value: '100K+', label: 'Bookings Made' },
              { value: '4.8', label: 'Average Rating' }
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-5xl font-bold mb-2">{stat.value}</p>
                <p className="text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
